import React from 'react';
import { TrendingUp, TrendingDown, Wallet, ArrowLeftRight } from 'lucide-react';
import { formatBRL } from '../lib/format';

/**
 * Three summary cards used above filtered lists.
 * Props: income, expense, balance (numbers). Optional label suffix.
 *
 * `expense` é a visão ORÇAMENTO (o gasto quando foi decidido). Quando há
 * cartão de crédito no projeto, `expenseCash` traz o desembolso — quanto de
 * fato saiu do caixa — e aparece como segunda linha. Sem cartão, os dois são
 * iguais e a linha extra é omitida, para não poluir a tela sem necessidade.
 */
export default function SummaryCards({ income = 0, expense = 0, expenseCash, balance, transfer = 0, transferCount = 0, suffix = '(Filtrado)', hide = false }) {
  if (hide) return null;
  const bal = balance == null ? income - expense : balance;
  const mostrarCaixa = expenseCash != null && Math.abs(expenseCash - expense) > 0.005;
  // Transferência não é receita nem despesa — é dinheiro mudando de lugar.
  // Somá-la ao saldo daria um número errado; escondê-la deixava o resumo de
  // uma seleção só de transferências zerado, sem informação nenhuma.
  const mostrarTransferencia = transferCount > 0 || Math.abs(transfer) > 0.005;
  return (
    <div className={`grid grid-cols-1 gap-4 ${mostrarTransferencia ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-3'}`} data-testid="summary-cards">
      <Card
        testid="summary-income"
        label={`Total de Receitas ${suffix}`}
        value={income}
        Icon={TrendingUp}
        tone="emerald"
      />
      <Card
        testid="summary-expense"
        label={`Total de Despesas ${suffix}`}
        value={expense}
        note={mostrarCaixa ? `Saiu do caixa: ${formatBRL(expenseCash)}` : null}
        Icon={TrendingDown}
        tone="rose"
      />
      <Card
        testid="summary-balance"
        label={`Saldo ${suffix}`}
        value={bal}
        Icon={Wallet}
        tone={bal < 0 ? 'rose' : 'indigo'}
        signed
      />
      {mostrarTransferencia && (
        <Card
          testid="summary-transfer"
          label={`Transferências ${suffix}`}
          value={transfer}
          note={`${transferCount} ${transferCount === 1 ? 'lançamento' : 'lançamentos'} · não altera o saldo`}
          Icon={ArrowLeftRight}
          tone="blue"
        />
      )}
    </div>
  );
}

function Card({ label, value, Icon, tone, signed = false, testid, note = null }) {
  const palette = {
    emerald: {
      bg: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-100 dark:border-emerald-900/40',
      icon: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-300',
      text: 'text-emerald-600 dark:text-emerald-300',
    },
    rose: {
      bg: 'bg-rose-50 dark:bg-rose-900/20 border-rose-100 dark:border-rose-900/40',
      icon: 'bg-rose-100 text-rose-600 dark:bg-rose-900/40 dark:text-rose-300',
      text: 'text-rose-600 dark:text-rose-300',
    },
    indigo: {
      bg: 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-100 dark:border-indigo-900/40',
      icon: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-300',
      text: 'text-indigo-700 dark:text-indigo-300',
    },
    blue: {
      bg: 'bg-blue-50 dark:bg-blue-900/20 border-blue-100 dark:border-blue-900/40',
      icon: 'bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300',
      text: 'text-blue-600 dark:text-blue-300',
    },
  }[tone];

  return (
    <div data-testid={testid} className={`rounded-2xl border ${palette.bg} p-5 flex items-start justify-between gap-3`}>
      <div className="min-w-0">
        <div className="text-sm text-slate-600 dark:text-slate-400">{label}</div>
        <div className={`mt-1 text-2xl md:text-3xl font-bold ${palette.text} truncate`}>
          {signed && value < 0 ? '-' : ''}{formatBRL(Math.abs(value))}
        </div>
        {note && (
          <div className="mt-1 text-xs text-slate-500 dark:text-slate-400 truncate" data-testid={`${testid}-note`}>
            {note}
          </div>
        )}
      </div>
      <div className={`w-12 h-12 shrink-0 rounded-xl flex items-center justify-center ${palette.icon}`}>
        <Icon className="w-6 h-6" />
      </div>
    </div>
  );
}
